import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { DynamicLogo } from "./DynamicLogo";
import { NAV } from "../data/content";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[110] transition-colors duration-300"
      style={{
        background: scrolled ? "color-mix(in srgb, var(--bg) 78%, transparent)" : "transparent",
        backdropFilter: scrolled ? "blur(18px)" : "none",
        borderBottom: scrolled ? "1px solid var(--line)" : "1px solid transparent",
      }}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1600px] px-6 sm:px-10 h-20 flex items-center justify-between">
        <DynamicLogo size="md" />

        <nav className="hidden md:flex items-center gap-9" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="relative text-sm uppercase tracking-widest transition-opacity"
              style={({ isActive }) => ({ color: isActive ? "var(--accent)" : "var(--text)", opacity: isActive ? 1 : 0.7 })}
            >
              {({ isActive }) => (
                <span className="group relative">
                  {item.label}
                  <span
                    className="absolute -bottom-1 left-0 h-px bg-current transition-all duration-300 group-hover:w-full"
                    style={{ width: isActive ? "100%" : "0%", background: "var(--accent)" }}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          data-testid="open-mobile-menu"
        >
          <Menu size={24} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[130] md:hidden"
            style={{ background: "var(--bg)" }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between h-20 px-6">
              <DynamicLogo size="md" />
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="close-mobile-menu" className="p-2">
                <X size={26} />
              </button>
            </div>
            <nav className="flex flex-col px-6 mt-10 gap-2">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}
                >
                  <NavLink
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="display text-5xl py-3 block"
                    style={{ color: "var(--text)" }}
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
